import { ChipLabel } from '@atoms/chipLabel';
import type { AtomModel } from '@models/atom.model';
import type { DNDBlock } from '@stratumn/studio-shared';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { observer } from 'mobx-react';
import {
  type NamedExoticComponent,
  type ReactElement,
  type ReactNode,
  useState
} from 'react';
import { type DragSourceMonitor, useDrag } from 'react-dnd';
import Dots from '@/assets/svg/dragDots.svg?react';
import { getCleanBlockType } from '@/utils/cleanBlockType';

import {
  BlockContainer,
  BlockHeader,
  BlockHeaderContent,
  BlockTitle,
  CollapseButton,
  ECSContainer,
  LeftSide,
  RightSide
} from './blockBase.style';
import BlockCondition from './blockCondition';
import { BlockBody, EndControlsContainer } from './body.block.style';

type BlockBaseProps = {
  dndBlock: DNDBlock;
  icon: LucideIcon | NamedExoticComponent;
  dataItem?: AtomModel<{ title: string }>;
  title?: string;
  endControls?: ReactElement;
  hasTitle?: boolean;
  isCondition?: boolean;
  isStatement?: boolean;
  header?: ReactNode;
  children?: ReactNode;
};

/** A base for all the DND blocks */
const BlockBase = ({
  dndBlock,
  icon: BlockIcon,
  endControls: EndControls,
  dataItem,
  title = '',
  hasTitle = true,
  isCondition = false,
  isStatement = false,
  header,
  children
}: BlockBaseProps) => {
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: dndBlock.type,
      item: { path: dndBlock.path, type: dndBlock.type },
      collect: (
        monitor: DragSourceMonitor<Partial<DNDBlock>, Partial<DNDBlock>>
      ) => ({
        isDragging: monitor.isDragging()
      })
    }),
    [dndBlock]
  );

  const [isHovering, setIsHovering] = useState(false);

  const isNested = dndBlock.path.split('/').length > 2;

  const toggleCollapse = (): void => {
    if (!dndBlock.blockState) {
      dndBlock.blockState = { isCollapsed: true };
      return;
    }

    if (dndBlock.blockState.isCollapsed === undefined) {
      dndBlock.blockState.isCollapsed = true; // first time toggling the block
      return;
    }

    dndBlock.blockState.isCollapsed = !dndBlock.blockState.isCollapsed;
  };

  const isCollapsed: boolean = !!dndBlock.blockState?.isCollapsed;

  const renderHeader = () => {
    if (header) {
      return header;
    }

    return (
      <>
        <ChipLabel className="text-[10px]" data-tag={`${dndBlock.type}-label`}>
          {getCleanBlockType(dndBlock.type)}
        </ChipLabel>
        {hasTitle && dataItem?.data?.title !== undefined && (
          <>
            <ChevronRight size={'0.8em'} />
            <BlockTitle
              onChange={e => (dataItem.data.title = e.target.value)}
              placeholder={`${getCleanBlockType(dndBlock.type)} title`}
              value={title}
            />
          </>
        )}
      </>
    );
  };

  if (isCondition) {
    return (
      <BlockCondition
        children={children}
        drag={drag}
        EndControls={EndControls}
        isCollapsed={isCollapsed}
        isCondition={isCondition}
        isDragging={isDragging}
        isHovering={isHovering}
        isNested={isNested}
        preview={preview}
        setHover={setIsHovering}
      />
    );
  }

  return (
    <BlockContainer
      $isCollapsed={isCollapsed}
      $isDragging={isDragging}
      $isNested={isNested}
      data-tag={`${dndBlock.type}-block`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      ref={preview}
    >
      <LeftSide
        className="group/drag transition-all"
        data-tag="drag-dots"
        ref={drag}
      >
        <div className="flex h-[25px] w-full shrink-0 items-center justify-center">
          <Dots className="h-5 text-gray-400 group-hover/drag:text-gray-600" />
        </div>
        {!isCollapsed && (
          <div className="h-full w-[2px] bg-gray-200 group-hover/drag:bg-gray-300" />
        )}
      </LeftSide>
      <RightSide>
        <BlockHeader $isStatement={isStatement}>
          <BlockHeaderContent>
            <BlockIcon color="#4A4A4A" size={20} />
            {renderHeader()}
          </BlockHeaderContent>
          {isStatement ? (
            <ECSContainer>
              <EndControlsContainer data-tag="end-control">
                {EndControls && (
                  <EndControls.type
                    {...EndControls.props}
                    isHovering={isHovering}
                  />
                )}
              </EndControlsContainer>
              <CollapseButton
                $isCollapsed={isCollapsed}
                onClick={() => toggleCollapse()}
              />
            </ECSContainer>
          ) : (
            <CollapseButton
              $isCollapsed={isCollapsed}
              onClick={() => toggleCollapse()}
            />
          )}
        </BlockHeader>
        {!isCollapsed && (
          <section className="flex w-full flex-col justify-between gap-4">
            <BlockBody $isStatement={isStatement}>{children}</BlockBody>
            {!isStatement && (
              <EndControlsContainer data-tag="end-control">
                {EndControls && (
                  <EndControls.type
                    {...EndControls.props}
                    isHovering={isHovering}
                  />
                )}
              </EndControlsContainer>
            )}
          </section>
        )}
      </RightSide>
    </BlockContainer>
  );
};

export default observer(BlockBase);
